import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

/**
 * Modo mantenimiento: freno de gasto.
 *
 * Lo activa solo la alerta de presupuesto (budget-guard.ts) o este botón.
 * Mientras está activo, las reglas de Firestore niegan todo salvo a los
 * administradores, así que las apps dejan de consumir lecturas.
 */
function MaintenanceCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => onSnapshot(
    doc(db, 'app_config', 'kill_switch'),
    (snap) => setState(snap.exists() ? snap.data() : { active: false }),
    (err) => { console.error(err); setError(err.message); },
  ), []);

  const toggle = async (activate) => {
    const confirmText = activate
      ? 'Se bloqueará la app para TODOS los usuarios (solo el panel seguirá funcionando). ¿Continuar?'
      : '¿Levantar el mantenimiento y restaurar las reglas normales?';
    if (!window.confirm(confirmText)) return;

    setBusy(true);
    setError(null);
    try {
      await httpsCallable(functions, activate ? 'activateMaintenance' : 'deactivateMaintenance')(
        activate ? { message: message.trim() } : {},
      );
      if (activate) setMessage('');
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const active = state?.active === true;
  const when = (ts) => (ts?.toDate ? ts.toDate().toLocaleString() : '');

  return (
    <div className="card" style={{ marginBottom: '1.5rem', borderColor: active ? 'var(--accent-red)' : undefined }}>
      <div className="card-header">
        <h3 className="card-title">{active ? '🛑 Modo mantenimiento ACTIVO' : '🟢 Servicio operativo'}</h3>
        {state && (
          <button
            className={`btn ${active ? 'btn-success' : 'btn-danger'}`}
            onClick={() => toggle(!active)}
            disabled={busy}
          >
            {busy ? '…' : active ? 'Reactivar servicio' : 'Activar mantenimiento'}
          </button>
        )}
      </div>

      {!state && !error && <div className="spinner" />}
      {error && <p className="text-sm" style={{ color: 'var(--accent-red)' }}>{error}</p>}

      {!active && state && (
        <input
          className="input-field"
          style={{ marginBottom: '0.75rem' }}
          value={message}
          maxLength={300}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Mensaje para los usuarios (opcional). Si lo dejas vacío se muestra el texto por defecto."
        />
      )}

      {active ? (
        <div className="text-sm">
          <p style={{ color: 'var(--accent-red)', fontWeight: 600 }}>
            La app está bloqueada para los usuarios. Solo el panel puede leer y escribir.
          </p>
          <p className="text-secondary" style={{ marginTop: '0.5rem' }}>
            Motivo: {state.reason === 'presupuesto_superado'
              ? `presupuesto superado (${state.costAmount} de ${state.budgetAmount} ${state.currencyCode || 'USD'})`
              : `activado a mano${state.activatedBy ? ' por ' + state.activatedBy : ''}`}
            {state.activatedAt ? ` · ${when(state.activatedAt)}` : ''}
          </p>
          {state.message && (
            <p className="text-sm" style={{ marginTop: '0.5rem' }}>
              Mensaje en la app: «{state.message}»
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-secondary">
          Se activa solo si el gasto del mes llega al presupuesto configurado en Google Cloud.
          {state?.deactivatedAt ? ` Último mantenimiento levantado el ${when(state.deactivatedAt)}.` : ''}
        </p>
      )}
    </div>
  );
}

export default MaintenanceCard;
